// NOTE: Max number of chat scopes retained in an in-memory chat store before the
// least-recently-written entry is evicted. Bounds session memory (attachments
// can hold File/ArrayBuffer) without a full LRU — the active and recently
// visited chats stay; long-abandoned scopes are dropped.
//
// NOTE: Shared by web and mobile (NSAI-4331). Both keep unsent chat state in a
// module-level Map and need the same bound, so the eviction rule lives here
// rather than being restated per app. Deliberately storage-free: these stores
// hold composer text, which carries pinned `/skill` tokens that must never
// reach disk (NSAI-3308/NSAI-3313), so the cap is about memory, not durability.
export const MAX_CHAT_SCOPE_ENTRIES = 25;

interface ISetScopedEntryProps<T> {
  store: Map<string, T>;
  key: string;
  value: T;
}

// NOTE: Set an entry and evict the oldest once the cap is exceeded. Re-inserting
// the key makes it most-recent (Map preserves insertion order), giving a cheap
// LRU-by-write so a frequently-updated active scope is never evicted first.
export const setScopedEntry = <T>({
  store,
  key,
  value,
}: ISetScopedEntryProps<T>): void => {
  store.delete(key);
  store.set(key, value);
  while (store.size > MAX_CHAT_SCOPE_ENTRIES) {
    const oldestKey = store.keys().next().value;
    if (oldestKey === undefined) break;
    store.delete(oldestKey);
  }
};
